/** The API's response shapes. Parsed with zod at the boundary so a drift fails loudly in one place. */
import { z } from 'zod'

export const STEP_STATUSES = ['pending', 'running', 'done', 'failed', 'skipped', 'manual'] as const
export const RUN_STATUSES = ['pending', 'running', 'done', 'failed'] as const
export const TEMPLATES = ['full', 'adopt', 'boxOnly', 'addPerson', 'teardown'] as const
export const ROLES = ['founder', 'teammate', 'viewer', 'admin'] as const

export type StepStatus = (typeof STEP_STATUSES)[number]
export type RunStatus = (typeof RUN_STATUSES)[number]
export type TemplateName = (typeof TEMPLATES)[number]

const iso = z.string()
const nullableIso = iso.nullable()

export const Step = z.object({
  key: z.string(),
  name: z.string(),
  status: z.enum(STEP_STATUSES),
  startedAt: nullableIso,
  finishedAt: nullableIso,
  ids: z.record(z.string(), z.unknown()).default({}),
  error: z.string().nullable(),
  by: z.string().nullable(),
})
export type Step = z.infer<typeof Step>

export const Run = z.object({
  id: z.string(),
  founderId: z.string(),
  template: z.enum(TEMPLATES),
  options: z.record(z.string(), z.unknown()).default({}),
  status: z.enum(RUN_STATUSES),
  steps: z.array(Step),
  error: z.string().nullable(),
  createdBy: z.string(),
  createdAt: iso,
  updatedAt: iso,
  finishedAt: nullableIso,
})
export type Run = z.infer<typeof Run>

/** What the SSE stream sends on every transition: a subset of the run. */
export const RunSnapshot = z.object({
  id: z.string(),
  status: z.enum(RUN_STATUSES),
  error: z.string().nullable(),
  updatedAt: iso,
  steps: z.array(Step),
})
export type RunSnapshot = z.infer<typeof RunSnapshot>

export const Founder = z.object({
  id: z.string(),
  name: z.string(),
  slug: z.string(),
  brand: z.record(z.string(), z.unknown()).default({}),
  // Plain strings for display values: a new status shows as text, it doesn't blank the page.
  status: z.string(),
  boxSize: z.string(),
  region: z.string(),
  subdomain: z.string().nullable(),
  createdAt: iso,
  updatedAt: iso,
})
export type Founder = z.infer<typeof Founder>

export const Person = z.object({
  id: z.string(),
  email: z.string(),
  name: z.string().nullable(),
  role: z.enum(ROLES),
})
export type Person = z.infer<typeof Person>

/** How a person reaches the founder's computer (board onboarding-r2, answer 6). */
export const ACCESS = ['shared', 'own'] as const
export type Access = (typeof ACCESS)[number]

/**
 * A person with their account and setup progress (GET /api/founder/people, and `people` on the founder detail). An
 * API from before teammates sends only the Person fields; the rest default to "nothing known".
 */
export const PersonView = Person.extend({
  access: z.enum(ACCESS).nullable().default(null),
  linuxUser: z.string().nullable().default(null),
  /** The last "Remove all devices" on their Linux user; null if never, or from an API before it. */
  devicesRemovedAt: nullableIso.default(null),
  signedIn: z.boolean().default(false),
  lastSeenAt: nullableIso.default(null),
  setupCompletedAt: nullableIso.default(null),
  /** Their latest Add person run; founders get no run id. */
  run: z
    .object({
      id: z.string().optional(),
      status: z.enum(RUN_STATUSES),
      createdAt: iso,
      steps: z.array(z.object({ key: z.string(), name: z.string(), status: z.enum(STEP_STATUSES) })),
    })
    .nullable()
    .default(null),
})
export type PersonView = z.infer<typeof PersonView>

export const PeopleList = z.object({ people: z.array(PersonView), canInvite: z.boolean() })
export type PeopleList = z.infer<typeof PeopleList>

/** POST /api/founder/people: a teammate, sharing the founder's account unless `ownUser`. */
export interface InviteInput {
  email: string
  name?: string
  ownUser: boolean
}
export const InviteResult = z.object({ run: z.object({ id: z.string().optional(), status: z.enum(RUN_STATUSES) }) })

export const Box = z.object({
  id: z.string(),
  provider: z.string(),
  dropletId: z.string().nullable(),
  region: z.string(),
  size: z.string(),
  ip: z.string().nullable(),
  status: z.string(),
  agentVersion: z.string().nullable(),
  lastHeartbeatAt: nullableIso,
  createdAt: iso,
})
export type Box = z.infer<typeof Box>

export const App = z.object({
  id: z.string(),
  origin: z.string(),
  repo: z.string().nullable(),
  installationId: z.string().nullable(),
  railwayWorkspace: z.string().nullable(),
  railwayProjectId: z.string().nullable(),
  railwayServiceId: z.string().nullable(),
  railwayEnvironmentId: z.string().nullable(),
  domain: z.string().nullable(),
  customDomain: z.string().nullable(),
  status: z.string(),
  createdAt: iso,
})
export type App = z.infer<typeof App>

/** The person's own setup marks (POST /api/me/progress). Null means not yet, or reset to run setup again. */
export const Progress = z.object({ setupCompletedAt: nullableIso, pairedAt: nullableIso })
export type Progress = z.infer<typeof Progress>
export const NO_SERVER_PROGRESS: Progress = { setupCompletedAt: null, pairedAt: null }
export const ProgressEnvelope = z.object({ progress: Progress })

export const Me = z.object({
  person: z.object({
    id: z.string(),
    email: z.string(),
    name: z.string().nullable(),
    role: z.enum(ROLES),
    founderId: z.string().nullable(),
  }),
  // The API sends it today; the founder contract only promises `person`.
  founder: Founder.nullable().optional(),
  // Absent from an API older than the invite gate: then nothing is on the server yet.
  progress: Progress.optional(),
})
export type Me = z.infer<typeof Me>

/** POST /api/waitlist: a signed-in person who isn't invited, recorded once. */
export const WaitlistJoin = z.object({ waitlisted: z.literal(true), since: iso })
export const WaitlistEntry = z.object({
  email: z.string(),
  name: z.string().nullable(),
  createdAt: iso,
  lastSeenAt: iso,
  /** True once an admin has added that email as a person. */
  invited: z.boolean(),
})
export type WaitlistEntry = z.infer<typeof WaitlistEntry>
export const Waitlist = z.object({ waitlist: z.array(WaitlistEntry) })

/**
 * GET /api/admin/counts: the nav badges in one call. `alertsOpen` is open and unacknowledged (High plus Medium);
 * `waitlist` not yet invited; `runsActive` pending or running; `boxesOffline` active with no heartbeat for 5 min.
 */
const n = z.number().int().nonnegative()
export const AdminCounts = z.object({
  alertsOpen: n,
  alertsHigh: n,
  alertsMedium: n,
  waitlist: n,
  runsActive: n,
  runsFailed24h: n,
  boxesOffline: n,
})
export type AdminCounts = z.infer<typeof AdminCounts>

export const FounderList = z.object({ founders: z.array(Founder) })

// ---------- Coaching (../api/docs/api.md, "Coaching"; privacy policy, "Coaching (optional)") ----------

/** The switch per founder company: off by default. `since` and `by` are the latest flip; null when never set. */
export const Coaching = z.object({ on: z.boolean(), since: nullableIso, by: z.string().nullable() })
export type Coaching = z.infer<typeof Coaching>
export const COACHING_OFF: Coaching = { on: false, since: null, by: null }
/**
 * What cpg.computer holds of the founder's conversations, as counts. Kept `keepDays` (90). Null when the API has no
 * store; absent from an API from before coaching reading.
 */
export const CoachingReceived = z.object({ sessions: z.number(), messages: z.number(), lastAt: nullableIso, keepDays: z.number() })
export type CoachingReceived = z.infer<typeof CoachingReceived>
/** GET and PUT /api/founder/coaching: the state, which way the caller may flip it, and the newest flips first. */
export const CoachingView = z.object({
  coaching: Coaching,
  /** `deleteData`: only the founder deletes what we received. An older API sends none; read that as no. */
  can: z.object({ turnOn: z.boolean(), turnOff: z.boolean(), deleteData: z.boolean().optional() }),
  history: z.array(z.object({ on: z.boolean(), at: iso, by: z.string() })),
  received: CoachingReceived.nullable().optional(),
  changed: z.boolean().optional(),
})
export type CoachingView = z.infer<typeof CoachingView>
/** DELETE /api/founder/coaching/data: what went, then the same view as GET. */
export const CoachingDeleted = CoachingView.extend({ deleted: z.object({ sessions: z.number(), messages: z.number() }) })
export type CoachingDeleted = z.infer<typeof CoachingDeleted>
/** An admin's delete on a founder's behalf: what went, and who on the founder's team was told. */
export const CoachingDeletedOnBehalf = z.object({ deleted: z.object({ sessions: z.number(), messages: z.number() }), told: z.array(z.string()) })
export type CoachingDeletedOnBehalf = z.infer<typeof CoachingDeletedOnBehalf>

export const COACHING_AGENTS = ['claude', 'codex', 'grok'] as const
const CoachingFounder = z.object({ id: z.string(), name: z.string(), slug: z.string() })
/** One conversation's metadata (the store keeps these plain; the words are sealed). `bytes` counts characters. */
export const CoachingSession = z.object({
  id: z.string(),
  // An agent this app doesn't know yet still lists, with its own name.
  agent: z.string(),
  /** The Linux user on the founder's box. */
  user: z.string(),
  /** The project's directory name. */
  project: z.string().nullable(),
  /** The first line of the first prompt. */
  title: z.string().nullable(),
  startedAt: iso,
  lastAt: iso,
  messages: z.number(),
  bytes: z.number(),
  /** Who works as that Linux user (a shared user has several). */
  people: z.array(z.object({ id: z.string(), email: z.string() })).default([]),
})
export type CoachingSession = z.infer<typeof CoachingSession>
/** GET /api/coaching/:founderId/sessions (admin): newest activity first, 200 at most. */
export const CoachingSessions = z.object({ founder: CoachingFounder, coaching: Coaching, keepDays: z.number(), sessions: z.array(CoachingSession) })
export type CoachingSessions = z.infer<typeof CoachingSessions>
export const CoachingMessage = z.object({
  id: z.string(),
  at: iso,
  role: z.enum(['user', 'assistant', 'tool']),
  toolName: z.string().nullable().default(null),
  text: z.string(),
})
export type CoachingMessage = z.infer<typeof CoachingMessage>
/** GET /api/coaching/:founderId/sessions/:sessionId (admin): one conversation in written order. */
export const CoachingSessionRead = z.object({ founder: CoachingFounder, coaching: Coaching, session: CoachingSession, messages: z.array(CoachingMessage) })
export type CoachingSessionRead = z.infer<typeof CoachingSessionRead>
/** Both admin reads while the switch is off: 403 with this body, so the page can name the founder. */
export const CoachingOffBody = z.object({ error: z.string(), code: z.literal('coaching_off'), founder: CoachingFounder, coaching: Coaching })
export type CoachingOffBody = z.infer<typeof CoachingOffBody>
/** An admin read as the page sees it: the answer, the switch off (with whose), or no store on this server (503). */
export type CoachingRead<T> = { state: 'on'; data: T } | { state: 'off'; founder: CoachingOffBody['founder']; coaching: Coaching } | { state: 'unavailable'; reason: string }

export const FounderDetail = z.object({
  founder: Founder,
  apps: z.array(App),
  boxes: z.array(Box),
  runs: z.array(Run),
  people: z.array(PersonView),
  /** Read-only for admins. An API from before the switch sends none; read that as off (coachingLine does). */
  coaching: Coaching.optional(),
})
export type FounderDetail = z.infer<typeof FounderDetail>

export const RunWithActive = z.object({ run: Run, active: z.boolean() })
export const RunEnvelope = z.object({ run: Run })
export const FounderEnvelope = z.object({ founder: Founder })
export const PersonEnvelope = z.object({ person: Person })

export const SizeOffer = z.object({
  slug: z.string(),
  memoryGb: z.number(),
  vcpus: z.number(),
  diskGb: z.number(),
  priceMonthly: z.number(),
  regions: z.array(z.string()),
  selectable: z.boolean(),
})
export type SizeOffer = z.infer<typeof SizeOffer>

export const Sizes = z.object({
  regions: z.array(z.object({ slug: z.string(), name: z.string() })),
  sizes: z.array(SizeOffer),
  fetchedAt: iso,
})
export type Sizes = z.infer<typeof Sizes>

/** One region a founder can pick, resolved by the API to a vendor region and size from live availability. */
export const PlanRegion = z.object({
  key: z.string(),
  label: z.string(),
  city: z.string(),
  tried: z.array(z.string()),
  available: z.boolean(),
  region: z.string().nullable(),
  size: z.string().nullable(),
  priceMonthly: z.number().nullable(),
  placement: z.string().nullable(),
})
export type PlanRegion = z.infer<typeof PlanRegion>

export const Plans = z.object({
  plan: z.object({ key: z.string(), name: z.string(), cores: z.number(), memoryGb: z.number(), diskGb: z.number(), summary: z.string() }),
  regions: z.array(PlanRegion),
  defaultRegion: z.string(),
  fetchedAt: iso,
})
export type Plans = z.infer<typeof Plans>

/** The API's error body: `{ error, code?, issues? }`. */
export const ErrorBody = z.object({
  error: z.string(),
  code: z.string().optional(),
  /** `not_invited` carries the email the API saw in the token. */
  email: z.string().optional(),
  issues: z.array(z.object({ path: z.string(), message: z.string() })).optional(),
})

// Request bodies, typed to match the API's zod (api/src/app/server.ts, runs.ts).
export interface CreateFounderInput {
  name: string
  slug: string
  region?: string
  boxSize?: string
  subdomain?: string
}
export interface CreatePersonInput {
  founderId?: string
  email: string
  name?: string
  role: 'founder' | 'teammate' | 'viewer'
}
export type CreateRunInput =
  | { founderId: string; template: 'full' | 'boxOnly'; options: { size?: string } }
  | {
      founderId: string
      template: 'adopt'
      options: {
        size?: string
        repo: string
        installationId?: string
        deploy: { workspace: string; projectId: string; serviceId: string; environmentId?: string }
      }
    }
  | { founderId: string; template: 'addPerson'; options: { email: string; name?: string; role?: 'founder' | 'teammate' | 'viewer'; ownUser?: boolean } }
  | { founderId: string; template: 'teardown'; options: Record<string, never> }

export type StepAction = 'run' | 'skip' | 'done'

// ---------- Founder side (docs/founder-api.md) ----------

/** The seven setup checks, in the machine's order. The API merges step-derived and agent checks into one list. */
export const CHECK_KEYS = ['machine', 'system', 'address', 'tools', 'user', 'phone', 'starter'] as const
export type CheckKey = (typeof CHECK_KEYS)[number]
export const CHECK_STATUSES = ['pending', 'ok', 'failing'] as const
export type CheckStatus = (typeof CHECK_STATUSES)[number]

export const Check = z.object({
  // A plain string: a check key the app doesn't know yet still shows, after the seven.
  key: z.string(),
  label: z.string(),
  status: z.enum(CHECK_STATUSES),
  detail: z.string().nullish(),
  at: nullableIso.optional(),
})
export type Check = z.infer<typeof Check>

/**
 * A run step as founders get it. The API trims steps for anyone who isn't an admin to key, name, status and times
 * (api founder-routes): no error text, ids or actor. Parsing them with the admin `Step` failed for every founder
 * (2026-10-12: "run.steps.0.error expected string, received undefined").
 */
export const FounderStep = Step.extend({
  ids: z.record(z.string(), z.unknown()).default({}),
  error: z.string().nullable().default(null),
  by: z.string().nullable().default(null),
})

/** The latest provisioning run, as GET /api/founder and the `run` event carry it. */
export const FounderRun = z.object({
  id: z.string(),
  template: z.enum(TEMPLATES),
  status: z.enum(RUN_STATUSES),
  steps: z.array(FounderStep),
  createdAt: iso.optional(),
  updatedAt: iso.optional(),
})
export type FounderRun = z.infer<typeof FounderRun>

export const FounderWorld = z.object({
  founder: z.object({
    id: z.string(),
    name: z.string(),
    slug: z.string(),
    subdomain: z.string().nullable(),
    status: z.string(),
    region: z.string(),
    boxSize: z.string(),
  }),
  box: z
    .object({
      id: z.string(),
      status: z.string(),
      region: z.string(),
      size: z.string(),
      ip: z.string().nullable(),
      lastHeartbeatAt: nullableIso,
      agentVersion: z.string().nullable(),
    })
    .nullable(),
  app: z.object({ domain: z.string().nullable(), repo: z.string().nullable(), deployStatus: z.string().nullable() }).nullable(),
  /** https://<slug>-preview.cpg.computer once the box's tunnel runs. Missing reads as null. */
  preview: z.object({ url: z.string() }).nullable().default(null),
  run: FounderRun.nullable(),
  checks: z.array(Check),
  /** The caller's Paseo devices, counts only (agent 0.4.0). Missing or null: not known. */
  devices: z.lazy(() => DeviceCount).nullable().default(null),
})
export type FounderWorld = z.infer<typeof FounderWorld>

/**
 * The agent's health report. The contract names the fields but not every unit, so each field is parsed on its own
 * and an unexpected shape reads as "not reported" instead of failing the whole heartbeat.
 * Usage: a percent (0 to 100), `{ used, total }` in any one unit, or `{ usedPercent }`.
 */
const Usage = z.union([z.number(), z.object({ used: z.number(), total: z.number() }), z.object({ usedPercent: z.number() })])
export type Usage = z.infer<typeof Usage>

export const Health = z.object({
  os: z.string().optional().catch(undefined),
  kernel: z.string().optional().catch(undefined),
  /** Seconds. */
  uptime: z.number().optional().catch(undefined),
  cpu: z
    .object({ load: z.union([z.number(), z.array(z.number())]).optional().catch(undefined), steal: z.number().optional().catch(undefined) })
    .optional()
    .catch(undefined),
  memory: Usage.optional().catch(undefined),
  disk: Usage.optional().catch(undefined),
  pendingUpdates: z.number().optional().catch(undefined),
  rebootRequired: z.boolean().optional().catch(undefined),
})
export type Health = z.infer<typeof Health>

export const Heartbeat = z.object({ at: iso, health: Health })
export type Heartbeat = z.infer<typeof Heartbeat>

/** The `run` event: the full run. Template is optional so an admin-style snapshot still merges. */
export const RunEvent = FounderRun.partial({ template: true })
export type RunEvent = z.infer<typeof RunEvent>
export const ChecksEvent = z.array(Check)

export const ProvisionResult = z.object({ run: FounderRun })
export const Pairing = z.object({ url: z.string(), qr: z.string() })

/**
 * "Remove all devices" (POST /api/founder/pairing/rotate): the Linux user re-keyed, when, how many people were on it,
 * and the caller's new link (null for an admin, or when the link failed and they ask again).
 */
export const RotateResult = z.object({ user: z.string(), rotatedAt: iso, people: z.number().int().nonnegative(), pairing: Pairing.nullable() })
export type RotateResult = z.infer<typeof RotateResult>
export type Pairing = z.infer<typeof Pairing>
export const ClaudeLogin = z.object({ sessionId: z.string(), url: z.string() })
export type ClaudeLogin = z.infer<typeof ClaudeLogin>
export const ClaudeCodeResult = z.object({ status: z.enum(['ok', 'failed']), detail: z.string().nullish() })
export type ClaudeCodeResult = z.infer<typeof ClaudeCodeResult>
export const ClaudeStatus = z.object({ loggedIn: z.boolean(), email: z.string().nullish() })
export type ClaudeStatus = z.infer<typeof ClaudeStatus>
/** Codex's device code: open `url`, sign in to ChatGPT, type `code` before `expiresAt`. */
export const CodexLogin = z.object({ url: z.string(), code: z.string(), expiresAt: iso })
export type CodexLogin = z.infer<typeof CodexLogin>
/** `pending`: the computer is still waiting for the person to finish signing in. */
export const CodexStatus = z.object({ loggedIn: z.boolean(), method: z.enum(['chatgpt', 'apiKey']).nullish(), pending: z.boolean().default(false) })
export type CodexStatus = z.infer<typeof CodexStatus>

// ---------- Person setup (GET /api/me/setup, board onboarding-r2) ----------

export const DeviceCount = z.object({ seen: z.number().int(), recent: z.number().int(), pushTokens: z.number().int() })
export type DeviceCount = z.infer<typeof DeviceCount>
export const PERSON_STEP_KEYS = ['signin', 'phone', 'agent', 'data', 'build', 'ship'] as const
export type PersonStepKey = (typeof PERSON_STEP_KEYS)[number]
export const AGENT_NAMES = ['claude', 'codex'] as const
export type AgentName = (typeof AGENT_NAMES)[number]
export const SetupStepView = z.object({
  key: z.enum(PERSON_STEP_KEYS),
  status: z.enum(['open', 'done', 'skipped']),
  how: z.enum(['observed', 'marked']).nullable(),
  at: nullableIso,
  by: z.string().nullish(),
  detail: z.string().nullish(),
})
export type SetupStepView = z.infer<typeof SetupStepView>
export const PersonSetup = z.object({
  path: z.enum(['self', 'whiteglove', 'adopt', 'teammate']),
  agent: z.enum(AGENT_NAMES),
  agentPicked: z.boolean(),
  completedAt: nullableIso,
  settled: z.boolean(),
  steps: z.array(SetupStepView),
  devices: DeviceCount.nullable(),
})
export type PersonSetup = z.infer<typeof PersonSetup>


// ---------- Connections (docs/founder-api.md, "Connections and secrets") ----------

export const ConnectionField = z.object({
  name: z.string(),
  label: z.string(),
  kind: z.enum(['text', 'secret']),
  help: z.string().nullish(),
})
export type ConnectionField = z.infer<typeof ConnectionField>

/** One way to connect (Shopify: a Dev Dashboard app's client pair, or an older custom app's token). */
export const ConnectionMode = z.object({
  key: z.string(),
  name: z.string(),
  fields: z.array(ConnectionField),
})
export type ConnectionMode = z.infer<typeof ConnectionMode>

/** How to get the credentials: steps a founder follows, a note (plans, who owns the key) and where to make it. */
export const ConnectionHelp = z.object({
  steps: z.array(z.string()),
  note: z.string().nullish(),
  link: z.object({ label: z.string(), url: z.string() }).nullish(),
})
export type ConnectionHelp = z.infer<typeof ConnectionHelp>

/**
 * A note on a working connection: it connected, and here is something it can't do yet, with the fix in `message`.
 * Shopify's `orders_last_60_days`: the app lacks read_all_orders, so orders older than 60 days don't come back.
 */
export const ConnectionWarning = z.object({ code: z.string(), message: z.string() })
export type ConnectionWarning = z.infer<typeof ConnectionWarning>

export const Connection = z.object({
  key: z.string(),
  name: z.string(),
  status: z.enum(['not_connected', 'connected', 'error']),
  /** The mode it's connected in. */
  mode: z.string().nullish(),
  detail: z.string().nullish(),
  connectedAt: nullableIso.optional(),
  /** Only on a connected connection that works with a gap. Absent from an API before warnings. */
  warnings: z.array(ConnectionWarning).optional(),
  /** When the last check ran, and the scopes Shopify said it granted then (Shopify only; older checks kept none). */
  checkedAt: nullableIso.optional(),
  scopes: z.array(z.string()).nullish(),
  /** The legacy mode's fields, kept for older clients. Read `modes`. */
  fields: z.array(ConnectionField),
  modes: z.array(ConnectionMode).optional(),
  help: ConnectionHelp.nullish(),
})
export type Connection = z.infer<typeof Connection>

export const Connections = z.object({ connections: z.array(Connection) })

/** One stored secret: a key set by hand, or a connection's (`connection` names it). Never a value. */
export const Secret = z.object({
  name: z.string(),
  connection: z.string().nullish(),
  /** What it's for, in the founder's words. */
  note: z.string().nullish(),
  setAt: iso,
  /** The email of whoever set it; null if that person is gone. */
  setBy: z.string().nullable(),
  /** When it last reached the app and the computer; null until the sync delivers it. */
  syncedAt: nullableIso,
})
export type Secret = z.infer<typeof Secret>
export const Secrets = z.object({ secrets: z.array(Secret) })

/** `PUT /api/founder/secrets/:name`: a value (with an optional note), or a note alone. */
export type SetSecretInput = { value: string; note?: string | null } | { note: string | null }

export const ShopifyConnectResult = z.object({
  status: z.literal('connected'),
  mode: z.string().nullish(),
  shop: z.object({ name: z.string(), domain: z.string(), currency: z.string().nullish() }),
  warnings: z.array(ConnectionWarning).optional(),
  scopes: z.array(z.string()).nullish(),
  checkedAt: nullableIso.optional(),
})
export type ShopifyConnectResult = z.infer<typeof ShopifyConnectResult>

/** Shopify's two modes: `clientCredentials` (Dev Dashboard app, preferred) and `accessToken` (older custom app). */
export type ShopifyMode = 'clientCredentials' | 'accessToken'

export type ShopifyConnectInput =
  | { storeDomain: string; clientId: string; clientSecret: string }
  | { storeDomain: string; accessToken: string }

/** `POST /api/founder/connections/triplewhale`: `shop` is the Triple Whale shop ID the key was checked against. */
export const TripleWhaleConnectResult = z.object({
  status: z.literal('connected'),
  mode: z.string().nullish(),
  shop: z.string(),
})
export type TripleWhaleConnectResult = z.infer<typeof TripleWhaleConnectResult>

/** Left out, `shopDomain` is the founder's Shopify store address (the API falls back to it). */
export type TripleWhaleConnectInput = { apiKey: string; shopDomain?: string }

// ---------- Fleet (admin; ../api/docs/api.md, "Fleet") ----------

export const FleetHealth = z.object({
  at: iso,
  load: z.number().nullable(),
  cores: z.number().nullable(),
  memoryPercent: z.number().nullable(),
  diskPercent: z.number().nullable(),
  pendingUpdates: z.object({ total: z.number(), security: z.number() }).nullable(),
  rebootRequired: z.boolean().nullable(),
})
export type FleetHealth = z.infer<typeof FleetHealth>

export const FailingCheck = z.object({ key: z.string(), label: z.string(), detail: z.string().nullable() })
export type FailingCheck = z.infer<typeof FailingCheck>

export const CloudInit = z.object({ status: z.string(), errors: z.array(z.string()) }).nullable()
export type CloudInit = z.infer<typeof CloudInit>

// ---------- Diagnosis (admin; ../api/docs/api.md, "Diagnosis") ----------

export const LIGHT_STATES = ['ok', 'warn', 'down', 'unknown'] as const
export type LightState = (typeof LIGHT_STATES)[number]

/** One traffic light: what one source says about one part of the box. */
export const Light = z.object({
  key: z.string(),
  label: z.string(),
  // A state this app doesn't know yet reads as unknown rather than failing the page.
  state: z.enum(LIGHT_STATES).catch('unknown'),
  detail: z.string(),
  at: nullableIso,
})
export type Light = z.infer<typeof Light>

export const ACTION_KEYS = ['restartBox', 'restartAgent', 'restartPaseo', 'restartPreview', 'redeployApp'] as const
export type ActionKey = (typeof ACTION_KEYS)[number]

export const FindingStep = z.object({ label: z.string(), action: z.enum(ACTION_KEYS).optional().catch(undefined), command: z.string().optional() })
export type FindingStep = z.infer<typeof FindingStep>

export const Finding = z.object({
  id: z.string(),
  severity: z.enum(['down', 'warn', 'info']).catch('warn'),
  title: z.string(),
  explanation: z.string(),
  steps: z.array(FindingStep),
})
export type Finding = z.infer<typeof Finding>

export const ActionView = z.object({ key: z.enum(ACTION_KEYS), label: z.string(), enabled: z.boolean(), reason: z.string().nullable() })
export type ActionView = z.infer<typeof ActionView>

/** A line of the audit log for an action an admin took on a box. */
export const ActionAudit = z.object({
  at: iso,
  actor: z.string(),
  action: z.string(),
  outcome: z.enum(['ok', 'failed']),
  message: z.string().nullish(),
})
export type ActionAudit = z.infer<typeof ActionAudit>

export const Diagnosis = z.object({
  at: iso,
  boxId: z.string(),
  founder: z.object({ id: z.string(), name: z.string(), slug: z.string() }),
  lights: z.array(Light),
  findings: z.array(Finding),
  actions: z.array(ActionView),
  recent: z.array(ActionAudit),
  actionWindowSec: z.number(),
})
export type Diagnosis = z.infer<typeof Diagnosis>

export const ActionResult = z.object({ action: z.string(), ok: z.boolean(), message: z.string(), audit: ActionAudit })
export type ActionResult = z.infer<typeof ActionResult>

export const SkillsState = z.object({ commit: z.string().nullable(), restored: z.array(z.string()), restoredAt: nullableIso })
export type SkillsState = z.infer<typeof SkillsState>

export const FleetRow = z.object({
  boxId: z.string(),
  founder: z.object({ id: z.string(), name: z.string(), slug: z.string() }),
  region: z.string(),
  size: z.string(),
  ip: z.string().nullable(),
  status: z.string(),
  agentVersion: z.string().nullable(),
  outdated: z.boolean(),
  lastHeartbeatAt: nullableIso,
  online: z.boolean(),
  offlineForSec: z.number().nullable(),
  connected: z.boolean(),
  health: FleetHealth.nullable(),
  cloudInit: CloudInit,
  /** The founder's coaching switch, read-only. An API from before the switch sends none; read that as off. */
  coaching: Coaching.optional(),
  failingChecks: z.array(FailingCheck),
  /** An API from before the diagnosis sends none. */
  lights: z.array(Light).default([]),
  /** Base skills in Claude Code's managed layer (agent 0.6.0): the template commit and the last restoration. */
  skills: SkillsState.nullable().optional(),
})
export type FleetRow = z.infer<typeof FleetRow>

export const Fleet = z.object({
  at: iso,
  onlineWithinSec: z.number(),
  latestAgentVersion: z.string().nullable(),
  boxes: z.array(FleetRow),
})
export type Fleet = z.infer<typeof Fleet>

// ---------- Alerts and webhooks (admin; ../api/docs/api.md, "Alerts" and "Webhooks") ----------

export const ALERT_KINDS = ['agent_offline', 'run_failed', 'deploy_failed', 'box_unhealthy'] as const
export const SEVERITIES = ['high', 'medium'] as const
export type Severity = (typeof SEVERITIES)[number]

const FounderRef = z.object({ id: z.string(), name: z.string(), slug: z.string() })

export const Alert = z.object({
  id: z.string(),
  // A kind this app doesn't know yet still shows, with its own words.
  kind: z.string(),
  severity: z.enum(SEVERITIES).catch('medium'),
  subject: z.string(),
  title: z.string(),
  detail: z.string(),
  founderId: z.string(),
  boxId: z.string().nullable(),
  runId: z.string().nullable(),
  openedAt: iso,
  resolvedAt: nullableIso,
  resolvedBy: z.string().nullable(),
  acknowledgedAt: nullableIso,
  acknowledgedBy: z.string().nullable(),
  notifyCount: z.number(),
  pushHeld: z.boolean().default(false),
  founder: FounderRef.nullable(),
  links: z.object({ alert: z.string(), founder: z.string(), run: z.string().optional() }),
})
export type Alert = z.infer<typeof Alert>

export const QuietHours = z.object({ start: z.number(), end: z.number(), timeZone: z.string() }).nullable()
export type QuietHours = z.infer<typeof QuietHours>

export const AlertList = z.object({
  alerts: z.array(Alert),
  counts: z.object({ open: z.number(), unacknowledged: z.number() }),
  quietHours: QuietHours,
  quietNow: z.boolean(),
})
export type AlertList = z.infer<typeof AlertList>

export const DELIVERY_EVENTS = ['alert.opened', 'alert.resolved', 'alert.reminder', 'webhook.test'] as const
export const Delivery = z.object({
  id: z.string(),
  webhookId: z.string(),
  alertId: z.string().nullable(),
  event: z.string(),
  status: z.enum(['pending', 'delivered', 'failed']).catch('failed'),
  attempts: z.number(),
  lastStatus: z.number().nullable(),
  lastError: z.string().nullable(),
  nextAttemptAt: nullableIso,
  createdAt: iso,
  deliveredAt: nullableIso,
  alert: z.object({ id: z.string(), title: z.string(), kind: z.string() }).nullish(),
})
export type Delivery = z.infer<typeof Delivery>

export const AlertEnvelope = z.object({ alert: Alert })
export const AlertDetail = z.object({ alert: Alert, deliveries: z.array(Delivery) })
export type AlertDetail = z.infer<typeof AlertDetail>

export const Webhook = z.object({
  id: z.string(),
  /** A hint: the origin and the last four characters. The API never returns the whole URL. */
  url: z.string(),
  description: z.string().nullable(),
  scope: z.enum(['global', 'founder']),
  founder: FounderRef.nullable(),
  active: z.boolean(),
  createdBy: z.string(),
  createdAt: iso,
  lastDelivery: z.object({ status: z.string(), event: z.string(), at: iso, lastStatus: z.number().nullable() }).nullable(),
})
export type Webhook = z.infer<typeof Webhook>

export const WebhookList = z.object({ webhooks: z.array(Webhook), configured: z.boolean() })
export type WebhookList = z.infer<typeof WebhookList>
export const WebhookEnvelope = z.object({ webhook: Webhook })
export const WebhookCreated = z.object({ webhook: Webhook, secret: z.string() })
export type WebhookCreated = z.infer<typeof WebhookCreated>
export const DeliveryList = z.object({ deliveries: z.array(Delivery) })
export const DeliveryEnvelope = z.object({ delivery: Delivery })
export const Ok = z.object({ ok: z.literal(true) })

export interface CreateWebhookInput {
  url: string
  founderId?: string
  description?: string
}
export interface AlertQuery {
  status?: 'open' | 'resolved' | 'all'
  founderId?: string
  kind?: string
  limit?: number
}

// ---------------------------------------------------------------- personal MCP tokens (../api/docs/api.md, "Personal MCP tokens")

/** One of the signed-in person's tokens. Never the value: that comes back once, on create. */
export const PersonalToken = z.object({
  id: z.string(),
  name: z.string(),
  /** The value's last four characters, to tell two tokens apart. */
  hint: z.string(),
  founder: z.object({ id: z.string(), slug: z.string().nullable(), name: z.string().nullable() }),
  createdAt: iso,
  expiresAt: nullableIso,
  lastUsedAt: nullableIso,
  expired: z.boolean(),
})
export type PersonalToken = z.infer<typeof PersonalToken>

/** A founder app the person may make a token for, with its MCP address (null without a subdomain). */
export const TokenApp = z.object({ id: z.string(), slug: z.string(), name: z.string(), mcpUrl: z.string().nullable() })
export type TokenApp = z.infer<typeof TokenApp>

export const TokenList = z.object({ tokens: z.array(PersonalToken), founders: z.array(TokenApp), limit: z.number() })
export type TokenList = z.infer<typeof TokenList>

export const TokenCreated = z.object({ token: PersonalToken, secret: z.string(), mcpUrl: z.string().nullable() })
export type TokenCreated = z.infer<typeof TokenCreated>

export interface CreateTokenInput {
  name: string
  founderId?: string
  /** 1 to 365; null never expires; left out, 90. */
  expiresInDays?: number | null
}

// ---------------------------------------------------------------- sharing (../api/docs/api.md, "Sharing")

/** One shareable screen of a founder app, from its own registry (never a draft). */
export const ShareScreen = z.object({ path: z.string(), title: z.string(), description: z.string() })
export type ShareScreen = z.infer<typeof ShareScreen>

/** GET /api/founder/screens: the live app's list, else the last one it gave (`saved`), with why. */
export const ShareScreens = z.object({
  app: z.object({ url: z.string() }).nullable(),
  screens: z.array(ShareScreen),
  source: z.enum(['live', 'saved', 'none']),
  checkedAt: nullableIso,
  reason: z.string().nullable(),
})
export type ShareScreens = z.infer<typeof ShareScreens>

export const Grantee = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('person'), email: z.string(), name: z.string().nullable(), signedUp: z.boolean() }),
  z.object({ kind: z.literal('group'), group: z.string(), label: z.string() }),
])
export type Grantee = z.infer<typeof Grantee>

export const Grant = z.object({
  id: z.string(),
  screen: z.string(),
  title: z.string().nullable(),
  grantee: Grantee,
  expiresAt: nullableIso,
  createdAt: iso,
  createdBy: z.string(),
  status: z.enum(['active', 'expired', 'revoked']),
})
export type Grant = z.infer<typeof Grant>

/** GET /api/founder/grants: unrevoked grants, newest first, and what the caller may do. */
export const GrantList = z.object({
  grants: z.array(Grant),
  groups: z.array(z.object({ id: z.string(), label: z.string() })),
  can: z.object({ share: z.boolean(), revoke: z.boolean() }),
})
export type GrantList = z.infer<typeof GrantList>

export const GrantCreated = z.object({ grant: Grant, created: z.boolean(), invited: z.boolean() })
export type GrantCreated = z.infer<typeof GrantCreated>

export const GrantRevoked = z.object({ revoked: z.boolean(), revokedAt: iso })

export type CreateGrantInput = { screen: string; expiresInDays?: number | null } & ({ email: string } | { group: 'listen' })

/** GET /api/me/shared: what is shared with the signed-in person, per founder app. */
export const SharedWithMe = z.object({
  apps: z.array(
    z.object({
      founder: z.object({ id: z.string(), name: z.string(), slug: z.string() }),
      url: z.string().nullable(),
      screens: z.array(z.object({ path: z.string(), title: z.string(), description: z.string(), url: z.string().nullable(), expiresAt: nullableIso })),
    }),
  ),
})
export type SharedWithMe = z.infer<typeof SharedWithMe>
// ---- Coding usage (../api usage-routes, board usage-r1) ----------------------------------------------------------

export const USAGE_AGENTS = ['claude', 'codex', 'grok'] as const
export const UsageAgentName = z.enum(USAGE_AGENTS)
export type UsageAgentName = z.infer<typeof UsageAgentName>
export const LimitKind = z.enum(['five_hour', 'weekly', 'weekly_scoped'])
export type LimitKind = z.infer<typeof LimitKind>

const usageTotals = {
  activeMinutes: z.number(),
  sessions: z.number(),
  commits: z.number(),
  /** Fresh tokens: input plus output. */
  tokens: z.number(),
  apiValueUsd: z.number(),
}
export const UsageWeek = z.object({
  ...usageTotals,
  dates: z.array(z.string()),
  days: z.array(z.object({ date: z.string(), day: z.string(), activeMinutes: z.number() })),
})
export type UsageWeek = z.infer<typeof UsageWeek>
export const UsageLimit = z.object({ kind: LimitKind, percent: z.number(), resetsAt: nullableIso, seenAt: iso })
export type UsageLimit = z.infer<typeof UsageLimit>
export const AgentWeek = z.object({
  agent: UsageAgentName,
  sessions: z.number(),
  activeMinutes: z.number(),
  tokens: z.object({ input: z.number(), output: z.number(), cacheRead: z.number(), cacheWrite: z.number() }),
  limits: z.array(UsageLimit),
  limitsKnowable: z.boolean(),
})
export type AgentWeek = z.infer<typeof AgentWeek>
/** GET /api/founder/usage: this week first, then the caller's own agents. */
export const FounderUsage = z.object({
  at: iso,
  tz: z.string(),
  week: UsageWeek.extend({ deploys: z.number().nullable() }),
  previousWeek: UsageWeek,
  me: z.object({ linuxUser: z.string(), week: UsageWeek, agents: z.array(AgentWeek) }).nullable(),
  people: z.array(z.object({
    personId: z.string(),
    name: z.string(),
    role: z.string(),
    you: z.boolean(),
    sharesUser: z.boolean(),
    activeMinutes: z.number(),
    sessions: z.number(),
    commits: z.number(),
  })),
  users: z.array(z.object({ linuxUser: z.string(), agents: z.array(AgentWeek) })).optional(),
})
export type FounderUsage = z.infer<typeof FounderUsage>

const RollingTotals = z.object({ ...usageTotals, lastActiveAt: nullableIso, agents: z.array(UsageAgentName) })
export const UsageSignalKey = z.enum(['idle', 'stuck', 'limited', 'stalled_setup'])
/** GET /api/admin/usage: per founder, the last 7 days against the 7 before, and the signals. */
export const AdminUsage = z.object({
  at: iso,
  founders: z.array(z.object({
    founder: z.object({ id: z.string(), name: z.string(), slug: z.string() }),
    boxId: z.string().nullable(),
    week: RollingTotals,
    previousWeek: z.object(usageTotals),
    deploys3d: z.number().nullable(),
    limitHits: z.number(),
    signals: z.array(z.object({ key: UsageSignalKey, text: z.string() })),
    users: z.array(RollingTotals.extend({ linuxUser: z.string() })),
  })),
})
export type AdminUsage = z.infer<typeof AdminUsage>
export type AdminUsageRow = AdminUsage['founders'][number]
